import type { ReactNode } from "react";
import { Redirect, useLocation } from "wouter";
import { useIsAuthenticated, useIsInitialized } from "@/stores/auth.store";
import { useHasPermission } from "@/hooks/use-permission";
import type { PermissionRequirement } from "@/types/rbac.types";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Se fornecido, exige que o user tenha pelo menos uma das permissions (OR). */
  requirements?: PermissionRequirement | PermissionRequirement[];
  /** Rota pra redirecionar se não tiver permissão (default: "/"). */
  forbiddenRedirect?: string;
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-3xl font-semibold text-foreground mb-2">Acesso negado</h1>
        <p className="text-muted-foreground">Você não tem permissão para acessar esta página.</p>
      </div>
    </div>
  );
}

/**
 * Protege uma rota por autenticação e opcionalmente por role/permission.
 *
 * @example
 * // Apenas autenticado
 * <ProtectedRoute><Dashboard /></ProtectedRoute>
 *
 * @example
 * // Autenticado + role ADMINISTRACAO ou BACKOFFICE
 * <ProtectedRoute requirements={[{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }]}>
 *   <UsersPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({ children, requirements, forbiddenRedirect }: ProtectedRouteProps) {
  const isAuthenticated = useIsAuthenticated();
  const isInitialized = useIsInitialized();
  const hasPermission = useHasPermission(requirements ?? []);
  const [location] = useLocation();

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    const redirectParam = location && location !== "/login" ? `?redirect=${encodeURIComponent(location)}` : "";
    return <Redirect to={`/login${redirectParam}`} />;
  }

  // Se não tem requirements, qualquer user autenticado acessa
  if (requirements && !hasPermission) {
    if (forbiddenRedirect) {
      return <Redirect to={forbiddenRedirect} />;
    }
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}
